import { Injectable } from '@angular/core';
import type { ProviderType } from '../../../models/project-integration.model';
import type { AcceptanceCriterionDraft, CoverageLinkDraft } from '../../../models/save-full-requirement.model';
import type {
  AutomationStatus,
  RequirementPriority,
  RequirementStatus,
  RequirementStatusOption,
  RequirementType,
} from '../../../models/requirement.model';
import type { StatusEditorEntry } from '../requirement-status-editor/requirement-status-editor.component';

const DRAFT_KEY = 'om-anforderung-draft';
const NEW_TC_KEY = 'om-anforderung-new-tc';

export interface RequirementFormDraftFormState {
  title: string;
  description: string;
  type: RequirementType;
  priority: RequirementPriority;
  status: RequirementStatus;
  ownerId: string | null;
  reviewerIds: string[];
  version: string;
  tags: string[];
  jiraIssueKey: string;
  notes: string;
  parentRequirementId: string;
  automationStatus: AutomationStatus;
  testFramework: string | null;
}

export interface RequirementFormDraft {
  requirementId: string;
  form: RequirementFormDraftFormState;
  acDrafts: AcceptanceCriterionDraft[];
  coverageDrafts: CoverageLinkDraft[];
  loadedReviewerIds: string[];
  providerUnlinked: boolean;
  selectedProviderType: ProviderType | null;
  statusEditorEntries: StatusEditorEntry[];
  baselineStatusValues: string[];
  statusOptions?: RequirementStatusOption[];
  pendingMigrations: Record<string, string>;
  criteriaLoadFailed?: boolean;
  coverageLoadFailed?: boolean;
}

interface NewTestCaseRecord {
  requirementId: string;
  testCaseId: string;
}

@Injectable({ providedIn: 'root' })
export class RequirementFormDraftStore {
  snapshot(draft: RequirementFormDraft): void {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ ...draft, savedAt: new Date().toISOString() }));
  }

  restore(requirementId: string): RequirementFormDraft | null {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as RequirementFormDraft;
      if (parsed.requirementId !== requirementId) return null;
      return parsed;
    } catch {
      sessionStorage.removeItem(DRAFT_KEY);
      return null;
    }
  }

  clear(): void {
    sessionStorage.removeItem(DRAFT_KEY);
  }

  rememberCreatedTestCaseId(requirementId: string, testCaseId: string): void {
    const record: NewTestCaseRecord = { requirementId, testCaseId };
    sessionStorage.setItem(NEW_TC_KEY, JSON.stringify(record));
  }

  consumeCreatedTestCaseId(requirementId: string): string | null {
    const raw = sessionStorage.getItem(NEW_TC_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(NEW_TC_KEY);
    try {
      const parsed = JSON.parse(raw) as NewTestCaseRecord;
      return parsed.requirementId === requirementId ? parsed.testCaseId : null;
    } catch {
      return null;
    }
  }
}
